import { useEffect, useRef } from 'react'

import { Box, Button, Radio, Text } from '@factorialco/deprecated-design-system'
import { Form as FactorialForm } from 'factorial-form'
import { SetRegularAccessStartDateDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Modal from 'modules/core/components/Modal'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ActionsEmployeeData } from 'modules/employees/components/Actions/types'
import { useUnterminateEmployee } from 'modules/employees/hooks/useUnterminateEmployee'

interface Props {
  employeeData: ActionsEmployeeData
  hasEmployeeOnboarding: boolean
  onClose: () => void
}

const ActivateModal = ({ employeeData, hasEmployeeOnboarding, onClose }: Props) => {
  const { unterminate } = useUnterminateEmployee()
  const employeeId = employeeData.id
  const hasRegularAccess = !hasEmployeeOnboarding || employeeData.hasRegularAccess
  const defaultValue = hasRegularAccess ? 'regular' : 'welcome_space'

  const form = useRef(
    new FactorialForm(
      {
        access_type: defaultValue,
      },
      {
        access_type: 'string',
      }
    )
  ).current

  async function unterminateEmployee() {
    const { data, error } = await unterminate({ id: employeeData.id })

    if (data?.employee?.id) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
        time: 2000,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: error || i18n.t('common.save_error'),
        time: 2000,
      })
    }
  }

  const [setRegularAccessStartDate] = useMutation(SetRegularAccessStartDateDocument, {
    extractorFn: (data) => data.employeesMutations.setRegularAccessStartDateEmployeesEmployee,
    update: (cache, { data }) => {
      const mutationResponse = data?.employeesMutations?.setRegularAccessStartDateEmployeesEmployee

      if (hasRegularAccess !== undefined) {
        cache.modify({
          id: cache.identify({ __typename: 'EmployeesEmployee', id: employeeId }),
          fields: {
            hasRegularAccess() {
              return mutationResponse?.employee?.hasRegularAccess
            },
          },
        })
      }
    },
  })

  const { running, performAction: onSubmit } = useAsyncAction(async () => {
    const regularAccessStartsOn = form.get('access_type').value === 'regular' ? moment() : undefined

    const result = await setRegularAccessStartDate({
      variables: { id: employeeId, startsOn: regularAccessStartsOn?.toISOString() },
    })

    const handled = handleResult(result, (errors) => {
      if (errors) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('employees.welcome_space.modal.error'),
        })
      }
    })

    if (handled.success) {
      await unterminateEmployee()
    }

    onClose()
  })

  useEffect(() => {
    if (!employeeData.isTerminating) {
      unterminateEmployee()
      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps we want to run this effect only once
  }, [])

  // if an employee termination date is in the future
  if (!employeeData.isTerminating) {
    return null
  }

  return (
    <Modal
      title={i18n.t('employees.untermination.modal.title')}
      description={i18n.t('employees.untermination.modal.description')}
      illustration="add-user"
      onSubmit={onSubmit}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} type="secondary" disabled={running}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={running} submit>
            {i18n.t('employees.untermination.modal.cta')}
          </Button>
        </>
      }
    >
      <FormRow>
        <Box gap="s8">
          <Text.Normal size="200" weight="medium" color="grey800">
            {i18n.t('employees.untermination.access_type_form.title')}
          </Text.Normal>
          <Radio
            name="welcome_space_access"
            description={i18n.t(
              'employees.untermination.access_type_form.welcome_space_option.description'
            )}
            option={{
              label: i18n.t('employees.untermination.access_type_form.welcome_space_option.label'),
              value: 'welcome_space',
            }}
            value={form.get('access_type').value}
            onChange={(value) => form.get('access_type').set(value)}
          />
          <Radio
            name="regular_access"
            description={i18n.t(
              'employees.untermination.access_type_form.regular_access_option.description'
            )}
            option={{
              label: i18n.t('employees.untermination.access_type_form.regular_access_option.label'),
              value: 'regular',
            }}
            value={form.get('access_type').value}
            onChange={(value) => form.get('access_type').set(value)}
          />
        </Box>
      </FormRow>
    </Modal>
  )
}

export default observer(ActivateModal)
